import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchBackofficeDashboard, type BackofficeDashboardStats } from '../api/stats'
import { contractsApi, type ContractReadinessSummary } from '../api/contracts'
import { Callout, Card, CardContent, CardHeader, LoadingState, Page, PageHeader, StatCard, StatGrid } from '../components/ui'

// TASK-0098's detection idiom (OutboxPage.tsx), reused verbatim: api() (client.ts) throws a plain
// Error whose message is either the backend's JSON body or, for a body-less 403 (exactly what
// [Authorize(PolicyNames.BackOffice)] returns), the fetch Response.statusText ("Forbidden").
function isForbidden(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : ''
  return msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')
}

// REQ-036-001/002: each riquadro is a queue of pratiche (or lotti) a backoffice/operator user
// might act on today. `to` is where the tile's click lands; most open /contracts pre-filtered by a
// single ContractStatus — ContractsPage.tsx's "Stato" filter is a single-value <select> (verified
// before writing this page), so a riquadro whose OWN definition spans more than one status can only
// deep-link to the more actionable one of the two, not both at once (riquadro "Pronte per lo
// scarico" is the one remaining case — see its own comment). See each tile's own comment for the
// reasoning, and the task report for the residual this leaves.
//
// Review round (P1-2, coordinator decision): "In attesa di approvazione" used to also sum lotti —
// now split into two riquadri (`pendingApproval` for pratiche, `batchesPendingApproval` for lotti,
// its own tile below) — EIGHT riquadri total, not seven. Review round (P1-3): "Da verificare" now
// counts DaVerificare only (see BackofficeDashboardController's remarks).
// Excludes `automationErrors` on purpose: it is the one optional/absent-when-denied field on
// BackofficeDashboardStats (riquadro 5), rendered separately below with its own `undefined` guard
// rather than through this always-present-value table.
type AlwaysPresentStatKey = Exclude<keyof BackofficeDashboardStats, 'automationErrors'>

const TILES: {
  key: AlwaysPresentStatKey
  title: string
  hint: string
  to: string
}[] = [
  {
    key: 'pendingVerification',
    title: 'Da verificare',
    hint: 'Pratiche in attesa di verifica backoffice',
    // P1-3 (review round): DaVerificare soltanto — combina il titolo, il conteggio e il link senza
    // il rischio di divergenza che InviataAlBackoffice avrebbe introdotto in futuro.
    to: '/contracts?status=DaVerificare',
  },
  {
    key: 'integrationRequested',
    title: 'Integrazioni richieste',
    hint: 'In attesa di documenti/correzioni dal richiedente',
    to: '/contracts?status=IntegrazioneRichiesta',
  },
  {
    key: 'readyForDispatch',
    title: 'Pronte per lo scarico',
    hint: 'Verificate, in attesa di assegnazione a un lotto',
    // Combines Verificata + InAttesaDiAssegnazioneScarico (vocabolario ADR-006 §2, nessuna
    // transizione applicata la raggiunge oggi — vale sempre 0). Verificata è quella cliccabile.
    to: '/contracts?status=Verificata',
  },
  {
    key: 'pendingApproval',
    title: 'In attesa di approvazione',
    hint: 'Pratiche in decisione di lotto',
    // P1-2 (review round): SOLO pratiche ApprovazioneRichiesta — i lotti hanno ora il loro
    // riquadro dedicato, subito sotto.
    to: '/contracts?status=ApprovazioneRichiesta',
  },
  {
    key: 'batchesPendingApproval',
    title: 'Lotti da approvare',
    hint: 'Lotti sottomessi, in attesa di un Manager',
    // NUOVO riquadro (P1-2, review round). Conta solo i lotti realmente sottomessi
    // (Batch.SubmittedAt != null) — un lotto ancora in assemblaggio non ha nulla su cui un
    // Manager possa decidere. Apre /batches (rotta esistente), non /contracts: sono entità
    // diverse.
    to: '/batches',
  },
  {
    key: 'operatorDrafts',
    title: 'Bozze operatore',
    hint: 'Bozze create dall’operatore, non ancora confermate',
    to: '/contracts?status=BozzaOperatoreCreata',
  },
  {
    key: 'staleOver48h',
    title: 'Ferme da oltre 48h',
    hint: 'Aperte (esclusa Bozza), nessun cambio di stato da più di 48 ore',
    // Nessun singolo ContractStatus descrive questo insieme (è "aperto, non Bozza" + "storico
    // vecchio") — non esiste un filtro /contracts che lo replichi, quindi il riquadro apre la
    // lista senza filtro di stato piuttosto che sceglierne uno impreciso. P2-1 (review round):
    // Bozza è esclusa da questo insieme — una pratica mai inviata è ferma per l'agente, non per
    // il backoffice.
    to: '/contracts',
  },
]

// Riquadro 5 ("Errori di automazione") is not shown here at all — see comment at its render site
// below: automationErrors is entirely ABSENT from the API response for a non-Admin caller, and the
// tile must not render for them (not render as a fake "0").

function fmtCount(n: number): string {
  return n.toLocaleString('it-IT')
}

export function BackofficeDashboardPage() {
  const [stats, setStats] = useState<BackofficeDashboardStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setUnauthorized(false)
    try {
      setStats(await fetchBackofficeDashboard())
    } catch (e) {
      if (isForbidden(e)) {
        setUnauthorized(true)
      } else {
        setError(e instanceof Error ? e.message : 'Errore')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // TASK-0281 (F-027, REQ-035-002): "Completezza pratiche (readiness)" — a SEPARATE, independently
  // failing fetch from `load()` above, same "own effect, does not gate the rest of the page" shape as
  // `attribution`/`commissionPreview` on ContractDetailPage.tsx. GET /api/contracts/readiness-summary
  // is `[Authorize(PolicyNames.BackOffice)]`, the SAME policy this whole page already requires for its
  // main stats — so `readinessSummaryForbidden` should not really be reachable once `stats` has loaded,
  // but is handled the same defensive way as everywhere else on this page rather than assumed away.
  // `readinessSummaryUnavailable` is the far more likely branch TODAY: F-026's migration is not yet
  // applied in production, so `crm.validation_rules` does not exist and this endpoint answers a
  // genuine server error — rendered as one calm sentence, never the raw exception text (same
  // reasoning as ContractDetailPage.tsx's own `readinessUnavailable`).
  const [readinessSummary, setReadinessSummary] = useState<ContractReadinessSummary | null>(null)
  const [readinessSummaryLoading, setReadinessSummaryLoading] = useState(true)
  const [readinessSummaryUnavailable, setReadinessSummaryUnavailable] = useState(false)
  const [readinessSummaryForbidden, setReadinessSummaryForbidden] = useState(false)

  useEffect(() => {
    setReadinessSummaryLoading(true)
    setReadinessSummaryUnavailable(false)
    setReadinessSummaryForbidden(false)
    contractsApi.getReadinessSummary()
      .then(setReadinessSummary)
      .catch((e: unknown) => {
        if (isForbidden(e)) {
          setReadinessSummaryForbidden(true)
          return
        }
        // Review fix: the fixed "non disponibile" sentence shown below is deliberate (see the block
        // comment above), but it must not swallow the real error — `load()` right above surfaces its
        // own message via `setError`. Logged, not shown to the user, so a genuine bug after the
        // migration lands still leaves a trace instead of hiding behind the same calm text forever.
        console.error('GET /api/contracts/readiness-summary failed:', e)
        setReadinessSummaryUnavailable(true)
      })
      .finally(() => setReadinessSummaryLoading(false))
  }, [])

  if (unauthorized) {
    return (
      <Page>
        <Callout tone="warning" role="alert">
          Non sei autorizzato a vedere la dashboard backoffice.
        </Callout>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader title="Cruscotto backoffice" icon="fact_check" />

      {error && <Callout tone="error">{error}</Callout>}

      {loading && !stats && <LoadingState />}

      {stats && (
        <StatGrid>
          {TILES.map(tile => {
            const value = stats[tile.key]
            const isZero = value === 0
            return (
              <Link key={tile.key} to={tile.to} className="text-decoration-none d-block h-100">
                {/* REQ-036-001: a zero must read as "niente in coda", not as a broken widget —
                    today's real data has several genuine zeros (riquadri Integrazioni
                    richieste, In attesa di approvazione, Bozze operatore). */}
                <StatCard className="h-100" label={tile.title} value={fmtCount(value)} supporting={isZero ? 'Niente in coda' : tile.hint} />
              </Link>
            )
          })}

          {/* Riquadro 5 — "Errori di automazione": StatsController/BackofficeDashboardController's
              "absent key, not a zeroed value" convention — automationErrors is missing entirely
              from the JSON for a caller who does not separately pass PolicyNames.Admin (see
              BackofficeDashboardController's remarks). Rendering a "0" for such a caller would
              claim automation is healthy when the true answer is "you can't know" — so the tile
              itself does not exist for them, exactly like OutboxPage.tsx's own nav entry is
              reachable but 403s for the same caller. */}
          {stats.automationErrors !== undefined && (
            <Link to="/outbox" className="text-decoration-none d-block h-100">
              <StatCard
                className="h-100"
                label="Errori di automazione"
                value={fmtCount(stats.automationErrors)}
                supporting={stats.automationErrors === 0 ? 'Niente in coda' : 'Righe outbox fallite'}
              />
            </Link>
          )}
        </StatGrid>
      )}

      {/* Completezza pratiche (readiness) — TASK-0281 (F-027, REQ-035-002). A NINTH riquadro,
          deliberately its own full-width row rather than squeezed into the StatGrid
          above: three counts plus a distribution bar need more room than one number, and this widget
          fetches independently (see the effect above) so it must not perturb the eight riquadri'
          layout or their own loading/error state. `readinessSummaryForbidden` hides the whole
          riquadro — same "tile does not exist for a caller who can't see it" convention as riquadro 5
          (`automationErrors`) right above, not a fake zero. */}
      {!readinessSummaryLoading && !readinessSummaryForbidden && (
        <Card variant="outlined">
          <CardHeader title="Completezza pratiche (readiness)" icon="checklist" />
          <CardContent>
            {readinessSummaryUnavailable && (
              <div className="md-typescale-body-small text-muted fst-italic">
                Il calcolo della completezza non è ancora disponibile per le pratiche di questo
                ambiente.
              </div>
            )}

            {readinessSummary && (
              <>
                {/* Calcolato in tempo reale dalle regole attive, sulle pratiche visibili al
                    chiamante: una distribuzione sbilanciata (anche 100% su un solo colore) è un
                    dato reale, non un widget rotto — questa riga lo rende esplicito, a prescindere
                    da quanto sia sbilanciata la distribuzione oggi o in futuro. */}
                <div className="md-typescale-body-small text-muted mb-3">
                  {fmtCount(readinessSummary.total)} pratiche valutate in tempo reale dal motore di
                  validazione attivo, sulle pratiche visibili.
                </div>

                {readinessSummary.total > 0 && (
                  <div
                    className="d-flex mb-3 md-u-track"
                    role="img"
                    aria-label={
                      `Rosso ${readinessSummary.rosso}, ` +
                      `Giallo ${readinessSummary.giallo}, ` +
                      `Verde ${readinessSummary.verde} ` +
                      `su ${readinessSummary.total} pratiche`
                    }
                  >
                    {readinessSummary.rosso > 0 && (
                      <div
                        style={{ width: `${(readinessSummary.rosso / readinessSummary.total) * 100}%`, background: 'hsl(var(--md-error))' }}
                      />
                    )}
                    {readinessSummary.giallo > 0 && (
                      <div
                        style={{ width: `${(readinessSummary.giallo / readinessSummary.total) * 100}%`, background: 'hsl(var(--md-warning))' }}
                      />
                    )}
                    {readinessSummary.verde > 0 && (
                      <div
                        style={{ width: `${(readinessSummary.verde / readinessSummary.total) * 100}%`, background: 'hsl(var(--md-success))' }}
                      />
                    )}
                  </div>
                )}

                <StatGrid>
                  <StatCard label="Incomplete" supporting="Rosso: dati mancanti" value={fmtCount(readinessSummary.rosso)} tone="error" />
                  <StatCard label="Da verificare" supporting="Giallo" value={fmtCount(readinessSummary.giallo)} tone="warning" />
                  <StatCard label="Pronte allo scarico" supporting="Verde" value={fmtCount(readinessSummary.verde)} tone="success" />
                </StatGrid>

                {readinessSummary.total === 0 && (
                  <div className="text-muted md-typescale-body-small mb-0">
                    Nessuna pratica visibile da valutare.
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* F-089 (TASK-0343): coda del backoffice — eccezioni, SLA, regole di priorità. */}
      <Link to="/backoffice/queue" className="text-decoration-none">
        <Card variant="outlined">
          <CardHeader title="Coda del backoffice" icon="playlist_add_check" />
          <CardContent>
            <div className="text-muted md-typescale-body-small">
              Eccezioni delle pratiche, SLA superati e regole di priorità
            </div>
          </CardContent>
        </Card>
      </Link>

      {/* F-018: Import esiti operatore */}
      <Link to="/backoffice/outcome-imports" className="text-decoration-none">
        <Card variant="outlined">
          <CardHeader title="Import esiti operatore" icon="upload_file" />
          <CardContent>
            <div className="text-muted md-typescale-body-small">
              Carica CSV e riconcilia anomalie da contratti operatore
            </div>
          </CardContent>
        </Card>
      </Link>
    </Page>
  )
}
